import SharedPreferences from "../constants/SharedPreferences";
import BayunError from "../constants/BayunError";
import { decryptAsymmetric } from "./AsymmetricEncryption";
import {
  getEncryptionCurveFromStorage,
  saveInInternalStorage,
  saveInStorage,
} from "./Utility";

/**
 * Restores the user signing identity from a login/recovery response.
 *
 * The user signing private key is wrapped to the user encryption public key, so it can only be
 * unwrapped once the user encryption private key has been recovered. Every login path must call
 * this: a session that restores only the encryption key can decrypt but cannot sign, and that
 * failure would otherwise surface much later inside an unrelated flow (app key pair creation,
 * secret creation, org settings) as a confusing "signing private key is required" error.
 *
 * @param {String} sessionId Session to store the recovered keys against.
 * @param {Object} responseData Login/recovery response carrying the wrapped signing key.
 * @param {String} userPrivateKey Already-recovered user encryption private key.
 * @param {String} userId Associated data used when the signing key was wrapped.
 */
export const restoreUserSigningKeys = async (
  sessionId,
  responseData,
  userPrivateKey,
  userId,
) => {
  const encryptedUserSigningPrivateKey =
    responseData?.encryptedUserSigningPrivateKey;
  const encryptedUserSigningPrivateKeyKek =
    responseData?.encryptedUserSigningPrivateKeyKek;
  const userSigningPublicKey = responseData?.userSigningPublicKey;

  // Fail closed rather than leaving a half-initialized session behind.
  if (
    !encryptedUserSigningPrivateKey ||
    !encryptedUserSigningPrivateKeyKek ||
    !userSigningPublicKey
  ) {
    console.log("User signing key material missing from response.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  const userSigningPrivateKey = await decryptAsymmetric(
    encryptedUserSigningPrivateKey,
    userPrivateKey,
    encryptedUserSigningPrivateKeyKek,
    userId,
    await getEncryptionCurveFromStorage(sessionId),
  );

  if (!userSigningPrivateKey) {
    console.log("UserSigningPrivateKey decryption failed.");
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }

  await saveInInternalStorage(
    sessionId,
    SharedPreferences.USER_SIGNING_PRIVATE_KEY,
    userSigningPrivateKey,
  );
  await saveInStorage(
    sessionId,
    SharedPreferences.USER_SIGNING_PRIVATE_KEY,
    userSigningPrivateKey,
  );

  await saveInStorage(
    sessionId,
    SharedPreferences.USER_SIGNING_PUBLIC_KEY,
    userSigningPublicKey.split("#")[0],
  );

  return userSigningPrivateKey;
};
